import { useState, useEffect } from "react";
import axios from "axios";
import { GetCookieData } from "../../Cookie/GetCookie";

const API_URL = process.env.REACT_APP_API_CORS;

export function useApproveUserFlow({ evaluate_salary_id, budget_year, round, token, autoStart = false, initialValue = null }) {
  const user = GetCookieData("userData");
  const [approveUserFlow, setApproveUserFlow] = useState(initialValue);
  const [flowLoading, setFlowLoading] = useState(false);
  const [nextLvApproveName, setNextLvApproveName] = useState("");

  const startEvaluateFlow = async () => {
    setFlowLoading(true);
    try {
      const url = `${API_URL}/api/evaluateSalaryFlow/startEvaluateFlow/${evaluate_salary_id}`;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(url, {}, config);
    } catch (error) {
      console.error("Error fetching approve user flow:", error);
    } finally {
      fetchApproveUserFlow();
    }
  };

  const fetchApproveUserFlow = async () => {
    setFlowLoading(true);
    try {
      const url = `${API_URL}/api/evaluateSalaryFlow/datatable`;
      const params = new URLSearchParams({
        page: 1,
        size: 10,
        "order[0]": "step",
        "order[1]": "ASC",
        "filter[evaluate_salary_id][0]": evaluate_salary_id,
        "filter[status][0]": 1,
      });
      const config = { headers: { Authorization: `Bearer ${token}` }, params };
      const response = await axios.get(url, config);

      if (autoStart && response.data.result.length === 0) {
        startEvaluateFlow();
      }
      setApproveUserFlow(response.data.result);
      if (autoStart) {
        setFlowLoading(false);
      }
    } catch (error) {
      console.error("Error fetching approve user flow:", error);
      if (autoStart) {
        setFlowLoading(false);
      }
    } finally {
      if (!autoStart) {
        setFlowLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchApproveUserFlow();
  }, []);

  useEffect(() => {
    if (approveUserFlow) {
      let thisApproveIndex = approveUserFlow?.findIndex(i => i.approve_user_id === user?.user_id);
      let nextApprove = approveUserFlow?.[thisApproveIndex + 1];
      setNextLvApproveName(nextApprove?.approve_user);
    }
  }, [approveUserFlow]);

  const handleChangeUser = (index, value) => {
    setApproveUserFlow(prev => {
      const isDuplicate = prev.some(
        (item, i) =>
          i !== index &&
          item.approve_user_id === value.user_id &&
          item.assessor !== 1 &&
          item.assessor !== 2
      );

      if (isDuplicate) {
        alert("ผู้ใช้นี้ถูกเลือกแล้ว");
        return prev;
      }

      const updated = prev.map((item, i) => {
        if (i === index) {
          return {
            ...item,
            approve_user_id: value.user_id,
            approve_user: value.user_name,
            approve_user_position_name: value.user_position_name,
            cont_to: value.cont_to,
          };
        }
        return item;
      });

      const reIndexed = updated.map((item, i) => ({
        ...item,
        step: i + 1,
      }));

      return reIndexed;
    });
  };

  const handleAddUserFlow = () => {
    setApproveUserFlow(prev => {
      const userFlowSet = [...prev];
      const index = userFlowSet.findIndex(u => u.assessor === 1);
      const newUser = { approve_user: "", approve_user_position_name: "", cont_to: "" };
      if (index !== -1) {
        userFlowSet.splice(index, 0, newUser);
      } else {
        userFlowSet.push(newUser);
      }
      return userFlowSet;
    });
  };

  const handleRemoveUserFlow = index => {
    setApproveUserFlow(prev => prev.filter((_, i) => i !== index));
  };

  const transformApproveFlow = flowArray => {
    if (!Array.isArray(flowArray) || flowArray.length === 0) return null;

    const approve_array = flowArray
      .filter(item => item.approve_user)
      .map((item, index) => ({
        approve_user_id: item.approve_user_id || null,
        approve_user: item.approve_user || "",
        approve_user_position_name: item.approve_user_position_name || "",
        cont_to: item.cont_to || "",
        step: index + 1,
      }));

    return {
      evaluate_salary_id: Number(evaluate_salary_id),
      budget_year: Number(budget_year),
      round: Number(round),
      approve_array,
    };
  };

  const handleEditFlow = async () => {
    setFlowLoading(true);
    try {
      const data = transformApproveFlow(approveUserFlow);
      const url = `${API_URL}/api/evaluateSalaryFlow/editFlow`;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(url, data, config);
    } catch (error) {
      console.error("Error fetching approve user flow:", error);
    } finally {
      fetchApproveUserFlow();
      setFlowLoading(false);
    }
  };

  const resetFlow = () => {
    fetchApproveUserFlow();
  };

  return {
    approveUserFlow,
    setApproveUserFlow,
    flowLoading,
    nextLvApproveName,
    fetchApproveUserFlow,
    handleChangeUser,
    handleAddUserFlow,
    handleRemoveUserFlow,
    handleEditFlow,
    resetFlow,
  };
}
